import React, { useEffect, useState } from 'react';
import {
  SimpleGrid,
  Text,
  HStack,
  VStack,
  Image,
  Box,
  Divider,
  useToast,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Tabs,
  TabList,
  Tab,
  Badge,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { SportsPageShell } from '../ui/SportsPageShell';
import { SportsCard } from '../ui/SportsCard';
import { MatchScoreHeader } from '../ui/MatchScoreHeader';
import { ResponsiveTableContainer } from '../ui/ResponsiveTableContainer';
import { useAppColors } from '../../hooks/useAppColors';
import { db } from '../../lib/firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { Match, LeagueId } from '../../types';
import { rankingService } from '../../services/rankingService';
import { useLeague } from '../../context/LeagueContext';
import { useAuth } from '../../context/AuthContext';
import { isUserParticipating } from '../../services/userParticipationService';
import {
  getJornadaLabel,
  getMundialGroupMatchday,
  getMundialGroupMatches,
  groupMundialKnockoutByPhase,
  isMundialGroupMatch,
  isMundialKnockoutMatch,
  parseLigaMxJornadaNumber,
  sortKnockoutPhases,
  toDate,
} from '../../services/matchdayService';
import { hydrateMundialMatchesAsync } from '../../services/mundial/mundialKnockoutHydrationService';

type MundialFilter =
  | null
  | { kind: 'group'; jornada: number }
  | { kind: 'knockout'; phase: string };

function isPredictionVisible(activo: unknown): boolean {
  return activo === 1 || activo === true;
}

function resolveCandidateUserIds(uid: string, uuid?: string): string[] {
  return [uid, uuid].filter(Boolean) as string[];
}

function belongsToUser(userId: string, candidateIds: string[]): boolean {
  return candidateIds.includes(userId);
}

interface FirestoreUser {
  id: string;
  name: string;
  email: string;
  displayName?: string;
  participacion?: Partial<Record<LeagueId, boolean>>;
}

interface FirestoreMatch extends Match {
  predictions: Array<{
    user: {
      name: string;
      avatar?: string;
    };
    predicted_score_a: number;
    predicted_score_b: number;
  }>;
}

const VALID_USERS = [
  { email: 'htorres@teiker.mx', name: 'HectorT' },
  { email: 'ralvarez@teiker.mx', name: 'Ramiro' },
  { email: 'ohernandez@teiker.mx', name: 'Oscar 🛌' },
  { email: 'jhernandez@teiker.mx', name: 'Chuy 🧑🏿‍🦱' },
  { email: 'erodriguez@teiker.mx', name: 'Neto' },
  { email: 'gmartinez@teiker.mx', name: 'Gus' },
  { email: 'lgama@teiker.mx', name: 'Gama 🧙🏻‍♂️' },
  { email: 'e.lugo@teiker.mx', name: 'Lugo 🎱' },
  { email: 'mrangel@teiker.mx', name: 'Alex' },
  { email: 'frojo@teiker.mx', name: 'Fer 🧂' },
  { email: 'lcardona@teiker.mx', name: 'Cardona' },
  { email: 'egonzalez@teiker.mx', name: 'Edgar' },
  { email: 'juanjo@juanjo.mx', name: 'JuanJo 👶🏼' },
  { email: 'alicia@teiker.mx', name: 'Alicia 🪦' },
  { email: 'cflores@teiker.mx', name: 'Charly' },
  { email: 'jmontemayor@teiker.mx', name: 'Jeannesy' },
  { email: 'palcala@teiker.mx', name: 'Paty' },
  { email: 'vtorres@teiker.mx', name: 'Vianey' },
  { email: 'calvarez@teiker.mx', name: 'Ivon' },
  { email: 'jherrera@teiker.mx', name: 'Pepe 💅🏻' }
];

const getUserAvatar = (name: string): string | undefined => {
  switch (name) {
    case 'Ramiro':
      return '/images/ramiro.png';
    case 'Gus':
      return '/images/el_gus.png';
    case 'Neto':
      return '/images/neto2.png';
    default:
      return undefined;
  }
};

const CapturedResults: React.FC = () => {
  const { league } = useLeague();
  const { user } = useAuth();
  const [matches, setMatches] = useState<FirestoreMatch[]>([]);
  const [mundialGroupPool, setMundialGroupPool] = useState<Match[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleMatchCount, setVisibleMatchCount] = useState(0);
  const toast = useToast();
  const [teamLogos, setTeamLogos] = useState<{ [teamName: string]: string }>({});
  const [selectedMundialFilter, setSelectedMundialFilter] = useState<MundialFilter>(null);
  const [selectedPhase, setSelectedPhase] = useState<string | null>(null);

  const ligaPhases = Array.from(new Set(matches.map((m) => m.phase)))
    .filter(Boolean)
    .sort(
      (a, b) =>
        (parseLigaMxJornadaNumber(a) ?? 0) - (parseLigaMxJornadaNumber(b) ?? 0) ||
        a.localeCompare(b)
    );

  const mundialGroupJornadasInView =
    league === 'mundial_fifa'
      ? Array.from(
          new Set(
            matches
              .filter(isMundialGroupMatch)
              .map((m) => getMundialGroupMatchday(m, mundialGroupPool))
          )
        ).sort((a, b) => a - b)
      : [];

  const mundialKnockoutPhasesInView =
    league === 'mundial_fifa'
      ? sortKnockoutPhases(
          Array.from(
            new Set(
              matches
                .filter(isMundialKnockoutMatch)
                .map((m) => m.phase)
                .filter(Boolean)
            )
          )
        )
      : [];

  const filteredMatches: FirestoreMatch[] =
    league === 'mundial_fifa'
      ? selectedMundialFilter === null
        ? matches
        : selectedMundialFilter.kind === 'group'
          ? matches.filter(
              (m) =>
                isMundialGroupMatch(m) &&
                getMundialGroupMatchday(m, mundialGroupPool) === selectedMundialFilter.jornada
            )
          : matches.filter(
              (m) =>
                isMundialKnockoutMatch(m) && m.phase === selectedMundialFilter.phase
            )
      : selectedPhase
        ? matches.filter((m) => m.phase === selectedPhase)
        : matches;

  const displaySections = buildDisplaySections(
    filteredMatches,
    league,
    mundialGroupPool
  );

  const { subtle } = useAppColors();

  useEffect(() => {
    setSelectedMundialFilter(null);
    setSelectedPhase(null);
    if (league && user) loadMatches();
    else {
      setMatches([]);
      setMundialGroupPool([]);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [league, user?.uid]);

  useEffect(() => {
    const fetchLogos = async () => {
      const logos: { [teamName: string]: string } = {};
      for (const match of matches) {
        if (match.team_a && !logos[match.team_a]) {
          logos[match.team_a] = (await rankingService.getTeamLogo(match.team_a)) || '';
        }
        if (match.team_b && !logos[match.team_b]) {
          logos[match.team_b] = (await rankingService.getTeamLogo(match.team_b)) || '';
        }
      }
      setTeamLogos(logos);
    };

    if (matches.length > 0) {
      fetchLogos();
    }
  }, [matches]);

  const loadMatches = async () => {
    if (!league || !user) return;
    setLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const candidateUserIds = resolveCandidateUserIds(
        user.uid,
        userDoc.data()?.uuid as string | undefined
      );

      const matchesRef = collection(db, 'matches');
      const matchesSnap = await getDocs(query(matchesRef, where('activo', '==', true)));
      const allMatches = matchesSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          league: data.league as LeagueId | undefined,
          phase: data.phase,
          group: data.group,
          jornada: data.jornada,
          match_type: data.match_type,
          bracket_slot: data.bracket_slot,
          source_slot_a: data.source_slot_a,
          source_slot_b: data.source_slot_b,
          team_a_code: data.team_a_code,
          team_b_code: data.team_b_code,
          team_a: data.team_a,
          team_b: data.team_b,
          score_a: data.score_a,
          score_b: data.score_b,
          activo: data.activo,
          team_a_logo: data.team_a_logo,
          team_b_logo: data.team_b_logo,
          date: data.date,
          is_first_leg: data.is_first_leg,
          created_at: data.created_at,
          updated_at: data.updated_at,
          predictions: [],
        } as FirestoreMatch;
      });
      let matchesData = allMatches.filter(
        (m) => m.league === league || (league === 'liga_mx' && !m.league)
      );
      if (league === 'mundial_fifa') {
        matchesData = (await hydrateMundialMatchesAsync(
          matchesData as Match[]
        )) as FirestoreMatch[];
      }
      const groupPool =
        league === 'mundial_fifa' ? getMundialGroupMatches(matchesData) : [];
      setMundialGroupPool(groupPool);

      const matchIds = new Set(matchesData.map(m => m.id));
      const currentUserMatchIds = new Set<string>();
      const predictionsRef = collection(db, 'predictions');
      const predictionsSnap = await getDocs(predictionsRef);
      const allPredictions: any[] = [];
      predictionsSnap.docs.forEach(docSnap => {
        const data = docSnap.data();
        const predLeague = data.league ?? 'liga_mx';
        const leagueMatch = predLeague === league || (league === 'liga_mx' && !data.league);
        // Estructura 1: predicción por partido
        if (data.match_id && matchIds.has(data.match_id) && leagueMatch) {
          if (belongsToUser(data.user_id, candidateUserIds)) {
            currentUserMatchIds.add(data.match_id);
          }
          if (isPredictionVisible(data.activo)) {
            allPredictions.push({ ...data, id: docSnap.id });
          }
        }
        // Estructura 2: array de predicciones por usuario
        if (Array.isArray(data.predictions)) {
          data.predictions.forEach((p: any) => {
            const pLeague = p.league ?? 'liga_mx';
            const pLeagueMatch = pLeague === league || (league === 'liga_mx' && !p.league);
            if (!matchIds.has(p.match_id) || !pLeagueMatch) return;
            if (belongsToUser(data.user_id, candidateUserIds)) {
              currentUserMatchIds.add(p.match_id);
            }
            if (isPredictionVisible(p.activo)) {
              allPredictions.push({ ...p, user_id: data.user_id });
            }
          });
        }
      });

      const visibleMatches = matchesData.filter((match) => currentUserMatchIds.has(match.id));
      setVisibleMatchCount(visibleMatches.length);

      // 3. Obtener todos los usuarios
      const usersRef = collection(db, 'users');
      const usersSnap = await getDocs(usersRef);
      const users = new Map<string, FirestoreUser>();
      usersSnap.docs.forEach(doc => {
        const userData = doc.data();
        if (userData.uuid && isUserParticipating(userData, league)) {
          users.set(userData.uuid, {
            id: doc.id,
            name: userData.name || '',
            email: userData.email || '',
            displayName: userData.displayName,
            participacion: userData.participacion,
          });
        }
      });

      // 4. Mapear predicciones a partidos (solo los que el usuario actual capturó)
      visibleMatches.forEach(match => {
        const matchPredictions = allPredictions.filter(
          (p) => p.match_id === match.id && users.has(p.user_id)
        );
        match.predictions = matchPredictions.map(prediction => {
          const user = users.get(prediction.user_id)!;
          const validUser = VALID_USERS.find(u => u.email === user?.email);
          
          return {
            user: {
              name: validUser?.name || user?.name || prediction.userName || 'Usuario Desconocido',
              avatar: getUserAvatar(validUser?.name || '')
            },
            predicted_score_a: prediction.predicted_score_a,
            predicted_score_b: prediction.predicted_score_b
          };
        });
      });

      setMatches(visibleMatches);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los datos',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Center py={20}>
        <Spinner size="xl" color="pitch.500" />
      </Center>
    );
  }

  if (!league) {
    return (
      <SportsPageShell title="Quinielas del grupo" subtitle="Comparativa de pronósticos">
        <Text color={subtle}>
          Selecciona una competencia en el menú para ver las quinielas capturadas.
        </Text>
      </SportsPageShell>
    );
  }

  if (!user) {
    return (
      <SportsPageShell title="Quinielas del grupo" subtitle="Comparativa de pronósticos">
        <Text color={subtle}>Inicia sesión para ver las quinielas capturadas del grupo.</Text>
      </SportsPageShell>
    );
  }

  const leagueLabel = league === 'mundial_fifa' ? 'Mundial FIFA 2026' : 'Liga BBVA MX';

  return (
    <SportsPageShell
      title="Quinielas capturadas"
      subtitle={`Compara pronósticos del grupo — ${leagueLabel}`}
      badge="SOCIAL"
    >
      <Text fontSize="sm" color={subtle} mb={4}>
        Solo ves los partidos que tú ya capturaste
        {visibleMatchCount > 0 ? ` (${visibleMatchCount})` : ''}.
      </Text>

      {visibleMatchCount === 0 && (
        <SportsCard mb={4}>
          <Text color={subtle} fontSize="sm">
            Aún no tienes pronósticos en partidos activos. Captura tu quiniela en inicio para
            comparar con el grupo.
          </Text>
        </SportsCard>
      )}
      <Tabs
        variant="sports"
        colorScheme="pitch"
        onChange={(idx) => {
          if (league === 'mundial_fifa') {
            if (idx === 0) {
              setSelectedMundialFilter(null);
              return;
            }
            const groupCount = mundialGroupJornadasInView.length;
            if (idx <= groupCount) {
              setSelectedMundialFilter({
                kind: 'group',
                jornada: mundialGroupJornadasInView[idx - 1],
              });
              return;
            }
            setSelectedMundialFilter({
              kind: 'knockout',
              phase: mundialKnockoutPhasesInView[idx - 1 - groupCount],
            });
          } else {
            setSelectedPhase(idx === 0 ? null : ligaPhases[idx - 1]);
          }
        }}
        isLazy
      >
        <TabList flexWrap="wrap" mb={4}>
          <Tab>Todas</Tab>
          {league === 'mundial_fifa' ? (
            <>
              {mundialGroupJornadasInView.map((jornada) => (
                <Tab key={`group-${jornada}`}>
                  {getJornadaLabel('mundial_fifa', jornada)}
                </Tab>
              ))}
              {mundialKnockoutPhasesInView.map((phase) => (
                <Tab key={`knockout-${phase}`}>{phase}</Tab>
              ))}
            </>
          ) : (
            ligaPhases.map((phase) => <Tab key={phase}>{phase}</Tab>)
          )}
        </TabList>
      </Tabs>

      {displaySections.map(({ key, label, sectionMatches }) => (
                <Box key={key} mb={8}>
                  <Badge mb={3} colorScheme="pitch">
                    {label}
                  </Badge>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    {sectionMatches.map((match) => (
                      <SportsCard key={match.id}>
                        <VStack spacing={4} align="stretch">
                          <MatchScoreHeader
                            teamA={match.team_a || ''}
                            teamB={match.team_b || ''}
                            scoreA={match.score_a}
                            scoreB={match.score_b}
                            teamLogos={teamLogos}
                          />
                          {match.bracket_slot && (
                            <Text fontSize="xs" color={subtle} fontFamily="mono">
                              {match.bracket_slot} · {match.id}
                            </Text>
                          )}

                          <Divider />

                          {match.predictions.length === 0 ? (
                            <Text color={subtle} fontSize="sm">
                              Sin predicciones aún.
                            </Text>
                          ) : (
                            <ResponsiveTableContainer>
                              <Table size="sm" variant="simple">
                              <Thead>
                                <Tr>
                                  <Th>Usuario</Th>
                                  <Th isNumeric>Pronóstico</Th>
                                </Tr>
                              </Thead>
                              <Tbody>
                                {match.predictions
                                  .filter((p) => p.user.name !== 'Usuario Desconocido')
                                  .map((prediction, idx) => (
                                    <Tr key={idx}>
                                      <Td>
                                        <HStack>
                                          <Text fontSize="sm">{prediction.user.name}</Text>
                                          {prediction.user.avatar && (
                                            <Image
                                              src={prediction.user.avatar}
                                              alt=""
                                              boxSize="22px"
                                            />
                                          )}
                                        </HStack>
                                      </Td>
                                      <Td isNumeric fontWeight="semibold">
                                        {prediction.predicted_score_a} –{' '}
                                        {prediction.predicted_score_b}
                                      </Td>
                                    </Tr>
                                  ))}
                              </Tbody>
                            </Table>
                            </ResponsiveTableContainer>
                          )}
                        </VStack>
                      </SportsCard>
                    ))}
                  </SimpleGrid>
                </Box>
              ))}
    </SportsPageShell>
  );
};

interface DisplaySection {
  key: string;
  label: string;
  sectionMatches: FirestoreMatch[];
}

function buildDisplaySections(
  matches: FirestoreMatch[],
  league: LeagueId | null,
  mundialGroupPool: Match[]
): DisplaySection[] {
  if (matches.length === 0) return [];

  if (league === 'mundial_fifa') {
    const sections: DisplaySection[] = [];
    const groupMatches = matches.filter(isMundialGroupMatch);
    const knockoutMatches = matches.filter(isMundialKnockoutMatch);

    const jornadasInView = Array.from(
      new Set(groupMatches.map((m) => getMundialGroupMatchday(m, mundialGroupPool)))
    ).sort((a, b) => a - b);

    jornadasInView.forEach((jornada) => {
      sections.push({
        key: `jornada-${jornada}`,
        label: getJornadaLabel('mundial_fifa', jornada),
        sectionMatches: groupMatches
          .filter((m) => getMundialGroupMatchday(m, mundialGroupPool) === jornada)
          .sort((a, b) => {
            const ga = a.group?.toUpperCase() ?? '';
            const gb = b.group?.toUpperCase() ?? '';
            if (ga !== gb) return ga.localeCompare(gb);
            return toDate(a.date).getTime() - toDate(b.date).getTime();
          }),
      });
    });

    groupMundialKnockoutByPhase(knockoutMatches).forEach((block) => {
      sections.push({
        key: `knockout-${block.phase}`,
        label: block.label,
        sectionMatches: block.matches as FirestoreMatch[],
      });
    });

    return sections;
  }

  const byPhase = new Map<string, FirestoreMatch[]>();
  matches.forEach((match) => {
    const phase = match.phase || '—';
    if (!byPhase.has(phase)) byPhase.set(phase, []);
    byPhase.get(phase)!.push(match);
  });

  return Array.from(byPhase.entries())
    .sort(([a], [b]) => {
      const na = parseLigaMxJornadaNumber(a) ?? 0;
      const nb = parseLigaMxJornadaNumber(b) ?? 0;
      return na - nb || a.localeCompare(b);
    })
    .map(([phase, sectionMatches]) => ({
      key: phase,
      label: phase,
      sectionMatches: sectionMatches.sort((a, b) => {
        if (a.is_first_leg && !b.is_first_leg) return -1;
        if (!a.is_first_leg && b.is_first_leg) return 1;
        return toDate(a.date).getTime() - toDate(b.date).getTime();
      }),
    }));
}

export default CapturedResults; 